import {
  setIsInPayment,
  setIsInPaymentBatch,
} from '@/lib/adapters/invoice-adapter';
import { createSepaPayment } from '@/lib/adapters/payment-adapter';
import { showNotification } from '@/lib/utils/showNotification';
import dayjs from 'dayjs';
import { FormInstance } from 'antd';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Invoice } from '@/lib/types';
import { Payment, PaymentInvoiceItem } from '@/lib/types/payment';
import {
  exceedsSepaBatchLimit,
  getEligibleSepaInvoices,
} from '../_helpers/inPaymentPageHelper';

type SepaPaymentEntry = {
  receiverIban: string;
  receiverBic?: string;
  receiverName?: string;
  amount: number;
  discount?: number;
  executionDate: Date;
  invoiceId: number;
};

type SepaNettingPaymentEntry = {
  invoiceId: number;
  receiverName?: string;
  nettingInvoiceId: number;
};

type SepaFormValues = {
  iban: string;
  isBatchSelected?: boolean;
  invoices?: Array<PaymentInvoiceItem | undefined>;
};

type UseInPaymentPageActionsParams = {
  form: FormInstance;
  t: TFunction;
  checkedList: number[];
  paymentsInProgress: Payment[] | undefined;
  refetchInvoices: () => Promise<unknown>;
  refetchPayments: () => Promise<unknown>;
  checkErrors: () => Promise<boolean>;
  setShowErrorMessages: Dispatch<SetStateAction<boolean>>;
  setCheckedList: Dispatch<SetStateAction<number[]>>;
  setInvoiceIds: Dispatch<SetStateAction<number[]>>;
};

export const useInPaymentPageActions = ({
  form,
  t,
  checkedList,
  paymentsInProgress,
  refetchInvoices,
  refetchPayments,
  checkErrors,
  setShowErrorMessages,
  setCheckedList,
  setInvoiceIds,
}: UseInPaymentPageActionsParams) => {
  const [sepaGenerateLoading, setSepaGenerateLoading] = useState(false);

  const createSepa = useCallback(
    async (values: SepaFormValues) => {
      if (paymentsInProgress?.length) {
        showNotification(
          'warning',
          t('api.payment.alreadyInProgress', { ns: 'errors' }),
        );
        return;
      }

      const includedInvoices = getEligibleSepaInvoices(
        values.invoices,
        checkedList,
      );

      try {
        setSepaGenerateLoading(true);
        if (!includedInvoices.length) {
          showNotification(
            'warning',
            t('api.payment.noInvoicesSelected', { ns: 'errors' }),
          );
          return;
        }

        if (exceedsSepaBatchLimit(includedInvoices)) {
          showNotification(
            'warning',
            t('api.payment.sepaMaxBatchAmount', { ns: 'errors' }),
          );
          return;
        }

        const paymentEntries: SepaPaymentEntry[] = [];
        const nettingPaymentEntries: SepaNettingPaymentEntry[] = [];

        for (const paymentInvoice of includedInvoices) {
          paymentEntries.push(
            ...paymentInvoice.partialPayments.flatMap((payment) => {
              if (!payment.bankAccount) {
                return [];
              }

              return [
                {
                  receiverIban: payment.bankAccount.iban,
                  receiverBic: payment.bankAccount.bic,
                  receiverName: paymentInvoice.receiverName,
                  amount: payment.value,
                  discount: paymentInvoice.discount,
                  executionDate: new Date(
                    dayjs(payment.date).format('YYYY-MM-DD'),
                  ),
                  invoiceId: paymentInvoice.invoice.invoiceId,
                },
              ];
            }),
          );

          nettingPaymentEntries.push(
            ...paymentInvoice.nettingInvoices.map((nettingInvoice) => ({
              invoiceId: nettingInvoice.invoiceId,
              receiverName: paymentInvoice.receiverName,
              nettingInvoiceId: paymentInvoice.invoice.invoiceId,
            })),
          );
        }

        if (!paymentEntries.length) {
          throw new Error('Minimaal 1 betaling moet gemaakt worden.');
        }

        await createSepaPayment({
          iban: values.iban,
          isBatchSelected: values.isBatchSelected ?? false,
          paymentEntries,
          nettingPaymentEntries,
        });
        showNotification('info', 'SEPA wordt gegenereerd.');
        await refetchPayments();
      } catch {
        showNotification(
          'error',
          'Fout tijdens genereren SEPA. Probeer opnieuw.',
        );
      } finally {
        setSepaGenerateLoading(false);
      }
    },
    [checkedList, paymentsInProgress, refetchPayments, t],
  );

  const showErrorNotification = useCallback(() => {
    setShowErrorMessages(true);
    showNotification('error', 'Nog niet alles is goed ingevuld');
  }, [setShowErrorMessages]);

  const handleSubmit = useCallback(async () => {
    const isValid = await checkErrors();

    if (!isValid) {
      showErrorNotification();
      return;
    }

    setShowErrorMessages(false);

    await createSepa({
      iban: form.getFieldValue('iban'),
      isBatchSelected: form.getFieldValue('isBatchSelected'),
      invoices: form.getFieldValue('invoices'),
    });
  }, [
    checkErrors,
    createSepa,
    form,
    setShowErrorMessages,
    showErrorNotification,
  ]);

  const handleSetIsInPaymentBatch = useCallback(async () => {
    try {
      await setIsInPaymentBatch(checkedList, false);
      await refetchInvoices();
      form.resetFields();
    } catch {
      showNotification('error', 'Verwijderen mislukt');
    }
  }, [checkedList, form, refetchInvoices]);

  const handleRemoveInvoice = useCallback(
    async (_invoiceIndex: number, invoice: Invoice) => {
      setCheckedList((prev) =>
        prev.filter((invoiceId) => invoiceId !== invoice.invoiceId),
      );
      setInvoiceIds((prev) =>
        prev.filter((invoiceId) => invoiceId !== invoice.invoiceId),
      );

      try {
        await setIsInPayment(invoice.invoiceId, !invoice.isInPayment);
        await refetchInvoices();
        form.resetFields();
      } catch (error) {
        showNotification('error', 'Verwijderen mislukt');
        throw error;
      }
    },
    [form, refetchInvoices, setCheckedList, setInvoiceIds],
  );

  const handleRemoveCreditInvoice = useCallback(
    async (_invoiceIndex: number, invoice: Invoice) => {
      try {
        await setIsInPayment(invoice.invoiceId, !invoice.isInPayment);
        await refetchInvoices();
      } catch (error) {
        showNotification('error', 'Verwijderen mislukt');
        throw error;
      }
    },
    [refetchInvoices],
  );

  const refreshPaymentsInProgress = useCallback(async () => {
    await refetchPayments();
    setSepaGenerateLoading(false);
  }, [refetchPayments]);

  return {
    sepaGenerateLoading,
    handleSubmit,
    handleSetIsInPaymentBatch,
    handleRemoveInvoice,
    handleRemoveCreditInvoice,
    refreshPaymentsInProgress,
  };
};
